import { useParams, useSearchParams } from "react-router";
import { MediaItemType } from "../../types/mediaItemType";
import useTranslation from "../../../../../packages/locales/translations/util/useTranslation";
import { useArticlesByProductArticleId } from "@coworker/app/src/components/RoomSettings/hooks/useArticlesByProductArticleId";
import { useMediaById } from "@coworker/app/src/components/RoomSettings/hooks/useMediaById";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useEDSData } from "@coworker/app/src/components/RoomSettings/hooks/useEDSData";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { useCombinationArticleData } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import { useSalesStopData } from "@coworker/app/src/components/RoomSettings/hooks/useSalesStopData";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import React, { useState } from "react";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { ArticleViewSkeleton } from "@coworker/app/src/components/RoomSettings/Skeletons/ArticleViewSkeleton";
import LocalCombinationSubArticlePanel from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationSubArticlePanel";
import {
  isArticleInCombination,
  isCombinationArticleValid,
} from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import { AggregatedItemListViewItem } from "../../components/aggregated-items/AggegatedItemListViewItem/AggregatedItemListViewItem";
import { ArticleListItemMobile } from "@coworker/app/src/components/RoomSettings/styles/styles";
import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import { useTasksByMedia } from "@coworker/app/src/components/RoomSettings/hooks/useTasksByMedia";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaPlacementSection } from "../../components/MediaPlacementSection/MediaPlacementSection";
import { AboutArticleSection } from "../../components/AboutArticleSection/AboutArticleSection";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { getMediaItemBadges } from "../../helpers/getMediaItemBadges";
import { ItemListViewItemCard } from "../../components/item-cards/ItemListViewItemCard/ItemListViewItemCard";
import { MediaItemViewType } from "../../types/mediaItemViewType";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import { MediaPlacementsModal } from "../../components/MediaPlacementSection/MediaPlacementsModal/MediaPlacementsModal";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";

export const MediaArticleInformationPage = () => {
  const { mediaId, articleId, productArticleId } = useParams();
  const [searchParams] = useSearchParams();

  const mediaItemType =
    (searchParams.get("media-item-type") as MediaItemType) || undefined;

  const { t } = useTranslation();
  const { data: articles, isLoading: isArticlesLoading } =
    useArticlesByProductArticleId(mediaId, productArticleId);

  const { data: mediaResponse, isLoading: isMediaLoading } =
    useMediaById(mediaId);

  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, productArticleId || "");
  const { data: itemInfo, isLoading: isItemInfoLoading } = useItemInfo(
    productArticleId ?? ""
  );

  const { data: taskTypes } = useAllTaskTypes();

  function getTaskTypeId(taskTypeName: TaskTypeNames) {
    return taskTypes?.find((type) => type.name === taskTypeName)?.id;
  }

  const storeId = useStoreId();
  const isMediaSubItem = mediaItemType === MediaItemType.LC_SUBITEM;
  // If media is a subitem, we need to fetch the combination article to show the parent article info
  const { data: combinationArticle } = useCombinationArticleData(
    articleId,
    isMediaSubItem
  );
  const { taskLinks } = useTasksByMedia(mediaId);

  const article =
    articles && articles.length > 0
      ? articles.filter((article) => article.id === articleId)[0]
      : undefined;

  const { data: salesStopData } = useSalesStopData(
    storeId,
    article?.productArticleId ?? ""
  );
  const {
    data: mediasAndArticleCount,
    isLoading: isMediasAndArticleCountLoading,
  } = useMediasAndArticleCount(productArticleId, mediaResponse?.media.storeId);

  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  const totalQuantityInPieces =
    mediasAndArticleCount
      ?.filter((media) => media.mediaId !== mediaId)
      .reduce((acc, media) => acc + media.nbrArticles, 0) || 0;

  const badges = React.useMemo(() => {
    const shouldShowProductBadges = false;
    if (article && mediaResponse?.media) {
      return getMediaItemBadges(
        article?.id,
        [article?.id],
        t,
        taskLinks,
        salesStopData?.find(
          (salesStop) => salesStop.productArticleId === article.productArticleId
        ) ?? null,
        mediaResponse?.media,
        shouldShowProductBadges
      );
    }
    return [];
  }, [article?.id, mediaResponse?.media, taskLinks, salesStopData, t]);

  const linkbar = React.useMemo(() => {
    if (!mediaResponse?.media.name || !mediaResponse?.media.id) {
      return <LinkbarLoading />;
    }

    const linkBarItems = [
      {
        label: t("mediaMaintenanceString"),
        to: "/mediamaintenance",
      },
      {
        label: mediaResponse.media.name,
        to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`,
      },
    ];

    if (mediaResponse.parent?.name) {
      linkBarItems.splice(1, 0, {
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}?tab=article-list-tab`,
      });
    }

    return (
      <Linkbar
        items={linkBarItems}
        currentLabel={article?.itemName ?? ""}
        currentAsH1
      />
    );
  }, [article?.itemName, mediaResponse, t]);

  const itemCardContent = React.useMemo(() => {
    if (!article || !mediaResponse) return null;
    return (
      <ItemListViewItemCard<Article>
        edsData={edsData?.[0] || null}
        mediaItemType={mediaItemType}
        showActions={false}
        itemCardRenderType={ItemCardRenderType.static}
        isItemInGroups={false}
        badges={badges}
        article={article}
        media={mediaResponse.media}
        mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
        itemInfo={itemInfo ?? null}
        itemInfoLoading={isItemInfoLoading}
        shouldDisplayQuantity={true}
        totalQuantityPieces={totalQuantityInPieces}
      />
    );
  }, [
    article,
    mediaResponse,
    edsData,
    mediaItemType,
    badges,
    itemInfo,
    isItemInfoLoading,
    totalQuantityInPieces,
  ]);

  if (isArticlesLoading || isMediaLoading)
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  if (!article || !mediaResponse) return null;

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      actionBarContent={
        <FixaButton
          type="primary"
          fluid
          text={t("createString")}
          onClick={() => setShowCreateTaskModal(true)}
        />
      }
    >
      {(isArticleInCombination(article) || isMediaSubItem) && (
        <LocalCombinationSubArticlePanel
          parentArticle={
            isCombinationArticleValid(combinationArticle)
              ? combinationArticle
              : undefined
          }
        />
      )}
      <ArticleListItemMobile>
        <AggregatedItemListViewItem
          article={
            {
              ...article,
              isFromArticleView: true,
              aggregatedArticles: articles,
            } as AggregatedArticle
          }
          media={mediaResponse.media}
          mediaItemType={mediaItemType ?? MediaItemType.REGULAR_ITEM}
          taskLinks={taskLinks}
          edsData={edsData?.[0] || null}
          itemCardRenderType={ItemCardRenderType.static}
          salesStopData={salesStopData?.[0] || null}
          itemInfo={itemInfo ?? null}
          itemInfoLoading={isItemInfoLoading}
        />
      </ArticleListItemMobile>
      {isMediasAndArticleCountLoading ? (
        <ArticleViewSkeleton />
      ) : (
        mediasAndArticleCount &&
        itemCardContent && (
          <MediaPlacementSection
            itemCardContent={itemCardContent}
            media={mediaResponse.media}
            mediasAndArticleCount={mediasAndArticleCount}
          />
        )
      )}
      <AboutArticleSection
        itemNoAndType={{
          no: article?.productArticleId ?? "",
          type: article?.productArticleType ?? "",
        }}
      />
      {mediasAndArticleCount && (
        <MediaPlacementsModal
          itemNoAndType={{
            no: article?.productArticleId ?? "",
            type: article?.productArticleType ?? "",
          }}
          showModal={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          mediasAndArticleCount={mediasAndArticleCount}
          title={t("createTaskString")}
          showCheckBox={true}
          showFooter={true}
          taskTypeId={getTaskTypeId(
            TaskTypeNames.MEDIA_MAINTENANCE_MULTIPLE_MEDIA
          )}
        />
      )}
    </FullScreenPopup>
  );
};
